import React, { useState, useEffect } from "react";
import { Typography, CircularProgress, Grid } from "@mui/material";
import axios from "axios";
import { StyledButton, StyledTypography } from "../styles/styles";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import Plot from "react-plotly.js";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const GenerateEIS = () => {
  const { selectedConfig } = useBatteryConfig();

  const [plotData, setPlotData] = useState({
    fig_json: null,
    module_plots: null,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleNavigateToSubmit = () => {
    navigate("/batteryscope/submit");
  };

  const handleNavigateToSoHAnalysis = () => {
    navigate("/batteryscope/soh-analysis");
  };

  useEffect(() => {
    if (!selectedConfig) return;

    const fetchPlotData = async () => {
      if (!selectedConfig.uploadData) {
        setError("No file selected");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("equivalent_circuit_params", JSON.stringify(selectedConfig.equivalent_circuit_params));

      try {
        const response = await axios.post(
          `${process.env.BACKEND_URL}batteries/battery_eis_plot_v2`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const { fig_json, module_plots } = response.data;

        if (!fig_json) {
          throw new Error("Plot data is incomplete or incorrect.");
        }
        setPlotData({
          fig_json,
          module_plots,
        });

        setError("");
        setLoading(false);
      } catch (error) {
        setError(error.message || "Error fetching plot data. Please check the data and try again.");
        setLoading(false);
      }
    };

    fetchPlotData();
  }, [selectedConfig]);

  return (
    <div className="visualize-data" style={{ textAlign: "center", padding: 20 }}>
      {!selectedConfig ? (
        <div className="mt-4">
          <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
            No Battery Data Selected
          </StyledTypography>
          <Typography variant="body1" gutterBottom className="mt-2 mb-4">
            To visualize battery data, please submit your battery configuration first.
          </Typography>
          <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
            Go to Submit Page
          </StyledButton>
        </div>
      ) : loading ? (
        <CircularProgress />
      ) : (
        <>
          {error ? (
            <>
              <Typography variant="body1" color="error" gutterBottom>
                {error}
              </Typography>
              <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                Go to Submit Page
              </StyledButton>
            </>
          ) : (
            <>
              <div style={{ height: "78vh", overflowY: "auto", width: "100%" }}>
                <div className="flex flex-col gap-3 w-full h-full">
                  <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                    {plotData.fig_json && (
                      <Plot
                        data={plotData.fig_json.data}
                        layout={{
                          ...plotData.fig_json.layout,
                          autosize: true,
                        }}
                        style={{ width: '100%', height: '100%' }}
                      />
                    )}
                  </div>
                  <div className="flex flex-row gap-3" style={{ width: "100%", overflowX: "auto" }}>
                    {plotData.module_plots && plotData.module_plots.length > 0 ? (
                      plotData.module_plots.map((plot, index) => (
                        <Plot
                          key={index}
                          data={plot.data}
                          layout={{
                            ...plot.layout,
                            autosize: true,
                            width: 550,
                            height: 400,
                          }}
                        />
                      ))
                    ) : (
                      <div className="flex flex-row justify-center text-center w-full">
                        <div className="border border-[#e8442d] rounded-lg p-4">
                          <StyledTypography color="error">EIS Module data was not present in your CSV.</StyledTypography>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                {/* <StyledTypography
                  variant="body1"
                  gutterBottom
                  className="mt-2 mr-2"
                  sx={{ color: "#e8442d", fontFamily: "Bai Jamjuree" }}
                >
                  Click on the plots to zoom in/out.
                </StyledTypography> */}
                <StyledButton
                  variant="contained"
                  onClick={handleNavigateToSoHAnalysis}
                  className="gap-2"
                >
                  Go to State of Health Analysis
                  <ArrowForwardIcon />
                </StyledButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GenerateEIS;
